/**
 * Structured logging service for the offline repository
 * Provides namespace-based logging with configurable levels
 */

import * as Effect from 'effect/Effect';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  namespace: string;
  message: string;
  data?: Record<string, unknown>;
  error?: Error;
}

export interface LoggerConfig {
  readonly minLevel: LogLevel;
  readonly namespaces: readonly string[]; // Only log from these namespaces (empty = all)
  readonly sinks: readonly LogSink[];
}

export interface LogSink {
  write(entry: LogEntry): Effect.Effect<void, never>;
}

// Console sink for development
export class ConsoleSink implements LogSink {
  write(entry: LogEntry): Effect.Effect<void, never> {
    return Effect.sync(() => {
      const prefix = `[${entry.timestamp}] [${entry.level.toUpperCase()}] [${entry.namespace}]`;
      const message = `${prefix} ${entry.message}`;

      switch (entry.level) {
        case 'debug':
          console.debug(message, entry.data || {});
          break;
        case 'info':
          console.info(message, entry.data || {});
          break;
        case 'warn':
          console.warn(message, entry.data || {}, entry.error);
          break;
        case 'error':
          console.error(message, entry.data || {}, entry.error);
          break;
      }
    });
  }
}

// In-memory sink for dev panel
export class MemorySink implements LogSink {
  private entries: LogEntry[] = [];
  private maxEntries: number = 1000;

  write(entry: LogEntry): Effect.Effect<void, never> {
    return Effect.sync(() => {
      this.entries.push(entry);
      if (this.entries.length > this.maxEntries) {
        this.entries.shift();
      }
    });
  }

  getEntries(): readonly LogEntry[] {
    return this.entries;
  }

  clear(): void {
    this.entries = [];
  }
}

export class Logger extends Context.Tag('Logger')<
  Logger,
  {
    readonly debug: (namespace: string, message: string, data?: Record<string, unknown>) => Effect.Effect<void, never>;
    readonly info: (namespace: string, message: string, data?: Record<string, unknown>) => Effect.Effect<void, never>;
    readonly warn: (namespace: string, message: string, data?: Record<string, unknown>, error?: Error) => Effect.Effect<void, never>;
    readonly error: (namespace: string, message: string, data?: Record<string, unknown>, error?: Error) => Effect.Effect<void, never>;
  }
>() {}

const levelPriority: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function shouldLog(config: LoggerConfig, level: LogLevel, namespace: string): boolean {
  // Check level
  if (levelPriority[level] < levelPriority[config.minLevel]) {
    return false;
  }

  // Check namespace filter
  if (config.namespaces.length > 0 && !config.namespaces.includes(namespace)) {
    return false;
  }

  return true;
}

function createLogEntry(
  level: LogLevel,
  namespace: string,
  message: string,
  data?: Record<string, unknown>,
  error?: Error
): LogEntry {
  return {
    timestamp: new Date().toISOString(),
    level,
    namespace,
    message,
    data,
    error,
  };
}

export const LoggerConfig = Context.GenericTag<LoggerConfig>('LoggerConfig');

export const LoggerLive = Layer.effect(
  Logger,
  Effect.gen(function* () {
    const config = yield* LoggerConfig;

    const log = (level: LogLevel, namespace: string, message: string, data?: Record<string, unknown>, error?: Error) => {
      if (!shouldLog(config, level, namespace)) {
        return Effect.void;
      }

      const entry = createLogEntry(level, namespace, message, data, error);

      // Write to all sinks
      const writes = config.sinks.map(sink => sink.write(entry));
      return Effect.all(writes, { concurrency: 'unbounded' }).pipe(
        Effect.asVoid
      );
    };

    return {
      debug: (namespace, message, data) => log('debug', namespace, message, data),
      info: (namespace, message, data) => log('info', namespace, message, data),
      warn: (namespace, message, data, error) => log('warn', namespace, message, data, error),
      error: (namespace, message, data, error) => log('error', namespace, message, data, error),
    };
  })
);

// Default configuration for development
export const defaultLoggerConfig: LoggerConfig = {
  minLevel: 'debug',
  namespaces: [], // Log all namespaces
  sinks: [new ConsoleSink()],
};

export const DefaultLoggerConfigLayer = Layer.succeed(LoggerConfig, defaultLoggerConfig);
export const DefaultLoggerLayer = Layer.provide(LoggerLive, DefaultLoggerConfigLayer);
